import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { openOrdersStream } from '../utils/ordersStream';

function ProcurementPage({ showToast }) {
  const [selectedDate, setSelectedDate] = useState(getTomorrowDate());
  const [weekDates, setWeekDates] = useState([]);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [rangeMode, setRangeMode] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    generateWeek(new Date(getTomorrowDate() + 'T00:00:00'));
  }, []);

  useEffect(() => {
    if (rangeMode) {
      if (dateFrom && dateTo) {
        loadData(dateFrom, dateTo);
      }
    } else if (selectedDate) {
      loadData(selectedDate, selectedDate);
    }
  }, [selectedDate, rangeMode, dateFrom, dateTo]);

  // SSE: автообновление при новом заказе
  useEffect(() => {
    // Поток заказов только для сотрудников: пропуск и переподключение — в openOrdersStream
    const es = openOrdersStream();
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const from = rangeMode ? dateFrom : selectedDate;
        const to = rangeMode ? dateTo : selectedDate;
        if (data.type === 'new_order' && data.delivery_date >= from && data.delivery_date <= to) {
          showToast(`Новый заказ #${data.order_number}`);
          loadData(from, to);
        }
      } catch (e) {}
    };
    return () => es.close();
  }, [selectedDate, rangeMode, dateFrom, dateTo]);

  function getTomorrowDate() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateValue(tomorrow);
  }

  function formatDateValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDateDisplay(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const day = date.getDate();
    const month = date.toLocaleDateString('ru-RU', { month: 'short' });
    return `${day} ${month}`;
  }

  function getDayName(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days[date.getDay()];
  }

  function generateWeek(centerDate) {
    const dates = [];
    const current = new Date(centerDate);
    const dayOfWeek = current.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    current.setDate(current.getDate() + diff);

    for (let i = 0; i < 7; i++) {
      dates.push(formatDateValue(current));
      current.setDate(current.getDate() + 1);
    }
    setWeekDates(dates);
  }

  function previousWeek() {
    const firstDate = new Date(weekDates[0] + 'T00:00:00');
    firstDate.setDate(firstDate.getDate() - 7);
    generateWeek(firstDate);
  }

  function nextWeek() {
    const firstDate = new Date(weekDates[0] + 'T00:00:00');
    firstDate.setDate(firstDate.getDate() + 7);
    generateWeek(firstDate);
  }

  function goToTomorrow() {
    const tomorrow = getTomorrowDate();
    generateWeek(new Date(tomorrow + 'T00:00:00'));
    setSelectedDate(tomorrow);
    setRangeMode(false);
  }

  const loadData = async (from, to) => {
    setIsLoading(true);
    try {
      const result = await api.getProcurement(from, to);
      setData(result);
      setExpandedRows(new Set());
    } catch (error) {
      showToast('Ошибка загрузки данных закупки', 'error');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (key) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return `${parts[2]}.${parts[1]}`;
  };

  const totalOrdered = data?.products?.reduce((s, p) => s + p.total_ordered, 0) || 0;
  const isRange = rangeMode || (data && data.date_from !== data.date_to);
  const todayStr = formatDateValue(new Date());

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">План закупки</h1>
      </div>

      {/* Навигация по неделям (flexWrap: на узком экране «Завтра» уходит вниз, а не за край) */}
      <div style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: 16,
        padding: '16px 0',
        borderBottom: '2px solid #EBEDEC'
      }}>
        <button
          type="button"
          className="btn-icon"
          onClick={previousWeek}
          title="Прошлая неделя" aria-label="Прошлая неделя"
          style={{ fontSize: 18, width: 38, height: 38 }}
        >
          ‹
        </button>

        {/* Дни — один переключатель .seg между стрелками, колонки равные (владелец 25.10).
            На узком экране лента прокручивается вбок, а не переносится рядами: перенос ломал
            бы вид группы. padding 1 — место для контура среза (::after выходит на 1 px) */}
        <div className="nv-hscroll-hidden" style={{ flex: 1, minWidth: 0, overflowX: 'auto', padding: 1 }}>
          <div className="seg" role="group" aria-label="День недели"
            style={{ display: weekDates.length ? 'grid' : 'none', gridAutoFlow: 'column', gridAutoColumns: 'minmax(64px, 1fr)', minWidth: 'max-content' }}>
            {weekDates.map(date => {
              const isToday = date === todayStr;
              const isSelected = !rangeMode && date === selectedDate;

              return (
                <button
                  key={date}
                  type="button"
                  aria-pressed={isSelected}
                  aria-current={isToday ? 'date' : undefined}
                  title={isToday ? 'Сегодня' : undefined}
                  className={`seg-btn${isSelected ? ' on' : ''}`}
                  onClick={() => {
                    setSelectedDate(date);
                    setRangeMode(false);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    fontWeight: isToday ? 600 : undefined
                  }}
                >
                  <span style={{ fontSize: 11, opacity: 0.8 }}>
                    {getDayName(date)}
                  </span>
                  {/* Сегодня — подчёркнутая терракотой дата вместо рамки 2px у одной кнопки:
                      видна и на белом, и на выбранном тёмном дне (владелец 25.10) */}
                  <span style={{
                    fontSize: 15, fontWeight: 600,
                    ...(isToday && { textDecoration: 'underline', textDecorationColor: '#ED956F', textDecorationThickness: 2, textUnderlineOffset: 4 })
                  }}>
                    {formatDateDisplay(date)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="btn-icon"
          onClick={nextWeek}
          title="Следующая неделя" aria-label="Следующая неделя"
          style={{ fontSize: 18, width: 38, height: 38 }}
        >
          ›
        </button>

        {/* «Завтра» — второстепенная: главная заливка здесь у выбранного дня (UX-аудит 25.10) */}
        <button
          type="button"
          className="btn btn-secondary btn-md"
          onClick={goToTomorrow}
          style={{ marginLeft: 8 }}
        >
          Завтра
        </button>
      </div>

      {/* Диапазон дат */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 24,
        flexWrap: 'wrap'
      }}>
        <label style={{ fontSize: 13, color: '#666' }}>Диапазон:</label>
        <input
          type="date"
          className="form-input"
          value={rangeMode ? dateFrom : selectedDate}
          onChange={(e) => {
            setRangeMode(true);
            setDateFrom(e.target.value);
            if (!dateTo || e.target.value > dateTo) {
              setDateTo(e.target.value);
            }
          }}
          style={{ width: 160, padding: '8px 12px', fontSize: 14 }}
        />
        <span style={{ color: '#999' }}>—</span>
        <input
          type="date"
          className="form-input"
          value={rangeMode ? dateTo : selectedDate}
          onChange={(e) => {
            setRangeMode(true);
            setDateTo(e.target.value);
            if (!dateFrom || e.target.value < dateFrom) {
              setDateFrom(e.target.value);
            }
          }}
          style={{ width: 160, padding: '8px 12px', fontSize: 14 }}
        />
        {rangeMode && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setRangeMode(false);
              setDateFrom('');
              setDateTo('');
            }}
            style={{ fontSize: 13 }}
          >
            Сбросить
          </button>
        )}
      </div>

      {/* Загрузка */}
      {isLoading && (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      )}

      {/* Результат */}
      {data && !isLoading && (
        <div className="card">
          {data.products.length > 0 ? (
            <div className="table-container" style={{ boxShadow: 'none' }}>
              <table className="table" style={{ fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={{ width: 30, padding: 10 }}></th>
                    <th style={{ padding: 10 }}>Товар</th>
                    <th style={{ padding: 10, textAlign: 'right', width: 90 }}>Заказано</th>
                    <th style={{ padding: 10, textAlign: 'right', width: 90, color: '#1F763B' }}>Склад</th>
                    <th style={{ padding: 10, textAlign: 'right', width: 90, color: '#26689A' }}>Поставка</th>
                    <th style={{ padding: 10, textAlign: 'right', width: 90 }}>Баланс</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.map((product) => {
                    const key = product.product_id || product.product_name;
                    const isExpanded = expandedRows.has(key);
                    return (
                      <React.Fragment key={key}>
                        <tr
                          onClick={() => toggleRow(key)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ padding: 10, fontSize: 12, color: '#999' }}>
                            {isExpanded ? '▼' : '▶'}
                          </td>
                          <td style={{ padding: 10, fontWeight: 500 }}>
                            {product.product_name}
                          </td>
                          <td style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>
                            {product.total_ordered}
                          </td>
                          <td style={{ padding: 10, textAlign: 'right', color: product.current_stock > 0 ? '#1F763B' : (product.current_stock < 0 ? '#CB4D45' : '#ccc') }}>
                            {product.current_stock !== 0 ? product.current_stock : '—'}
                          </td>
                          <td style={{ padding: 10, textAlign: 'right', color: product.planned_stock > 0 ? '#26689A' : '#ccc' }}>
                            {product.planned_stock > 0 ? product.planned_stock : '—'}
                          </td>
                          <td style={{
                            padding: 10,
                            textAlign: 'right',
                            fontWeight: 700,
                            color: product.balance >= 0 ? '#1F763B' : '#CB4D45'
                          }}>
                            {product.balance >= 0 ? `+${product.balance}` : product.balance}
                          </td>
                        </tr>

                        {isExpanded && product.orders.map((order, idx) => (
                          <tr key={`${key}-${order.order_id}-${idx}`}
                              style={{ background: '#F9F9F6' }}>
                            <td style={{ padding: '8px 10px' }}></td>
                            <td style={{ padding: '8px 10px', color: '#666', fontSize: 13 }}>
                              Заказ #{order.order_id}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 13, color: '#666' }}>
                              {order.quantity} шт
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 13, color: '#666' }}>
                              {isRange ? formatDateShort(order.delivery_date) : ''}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 13, color: '#666' }}>
                              {order.delivery_time || '—'}
                            </td>
                            <td style={{ padding: '8px 10px' }}></td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  <tr style={{ borderTop: '2px solid var(--primary-color)', fontWeight: 700 }}>
                    <td style={{ padding: 10 }}></td>
                    <td style={{ padding: 10 }}>Итого</td>
                    <td style={{ padding: 10, textAlign: 'right' }}>{totalOrdered}</td>
                    <td style={{ padding: 10 }}></td>
                    <td style={{ padding: 10 }}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
              Нет оплаченных заказов на выбранные даты
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProcurementPage;
